// ===== AppShell: Sidebar + Header + QuickSearch + FAB =====
import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import { PERMISSIONS, ROLE_LABELS, usePermissions } from './auth.jsx';
import { LEADS, STAGE_NAMES, brlCompact } from './data.jsx';
import { encerrarSessao } from './login.jsx';
import { estaEmDesenvolvimento } from './tela-em-desenvolvimento.jsx';

const { useState: useStateShell } = React;

const ID_TO_ROUTE = {
  resumo: '/', pipeline: '/pipeline', clientes: '/clientes',
  produtos: '/produtos', alerts: '/tarefas', posvenda: '/pos-venda',
  sac: '/sac', reports: '/relatorios', gestao: '/gestao',
  admin: '/admin', ajustes: '/ajustes', webhooks: '/webhooks', logs: '/logs',
  financeiro: '/financeiro',
};

const NAV = [
  { id: "resumo",   label: "Resumo",     icon: "sparkles",       group: "main" },
  { id: "pipeline", label: "Pipeline",   icon: "layout-kanban",  group: "main" },
  { id: "clientes", label: "Clientes",   icon: "users",          group: "main" },
  { id: "produtos", label: "Produtos",   icon: "package",        group: "main" },
  { id: "alerts",   label: "Tarefas",    icon: "check-circle-2", group: "main", counter: 4 },
  { id: "posvenda", label: "Pós-venda",  icon: "rocket",         group: "main" },
  { id: "sac",      label: "SAC",        icon: "life-buoy",      group: "main" },
  { id: "reports",  label: "Relatórios", icon: "bar-chart-2",    group: "main" },
  { id: "financeiro", label: "Financeiro", icon: "wallet",       group: "admin", roleGate: ["Admin", "Diretor", "Financeiro"] },
  { id: "gestao",   label: "Gestão",     icon: "target",         group: "admin", roleGate: ["Admin", "Backoffice"] },
  { id: "ajustes",  label: "Ajustes",    icon: "settings",       group: "admin", roleGate: ["Admin", "Diretor", "Backoffice"] },
  { id: "admin",    label: "Admin",      icon: "shield-check",   group: "admin", roleGate: ["Admin", "Backoffice"] },
  { id: "webhooks", label: "Webhooks",   icon: "zap",            group: "admin", roleGate: ["Admin"] },
  { id: "logs",     label: "API Logs",   icon: "terminal",       group: "admin", roleGate: ["Admin"] },
];

const NAV_PERM = {
  resumo:   'dashboard.view',
  pipeline: 'pipeline.view',
  clientes: 'clientes.view',
  produtos: 'produtos.view',
  alerts:   'tarefas.view',
  posvenda: 'posvenda.view',
  sac:      'sac.view',
  reports:  'relatorios.view_comissoes',
  financeiro: 'financeiro.view',
  gestao:   'gestao.view',
  ajustes:  'ajustes.view',
  admin:    'admin.view',
  webhooks: 'admin.view',
  logs:     'admin.view',
};

function navAllowedFor(papel, id) {
  const perm = NAV_PERM[id];
  if (!perm) return false; // sem regra declarada, nega — nada entra no menu por omissão
  return (PERMISSIONS[perm] && PERMISSIONS[perm][papel]) === true;
}

function Sidebar({ alertCount, pinned, onPin, onNavigate }) {
  const store = useStore();
  const papel = (store.me?.papel ?? 'admin').toLowerCase();
  const navAllowed = (item) => navAllowedFor(papel, item.id);
  const main = NAV.filter(n => n.group === "main" && navAllowed(n));
  const admin = NAV.filter(n => n.group === "admin" && navAllowed(n));

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <div className="logo">P</div>
        <div className="brand-text">
          <div className="brand-name">BeamCRM</div>
        </div>
        <button className="sidebar-pin-btn" onClick={onPin} title={pinned ? "Recolher menu" : "Fixar menu expandido"}>
          <Icon name={pinned ? "chevron-left" : "chevron-right"} size={14} />
        </button>
      </div>
      <nav className="sidebar-nav">
        <div className="nav-group-label">Vendas</div>
        {main.map(n => (
          <NavLink key={n.id} to={ID_TO_ROUTE[n.id] || '/'} end={n.id === 'resumo'} onClick={onNavigate}
            className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
            <Icon name={n.icon} size={16} />
            <span>{n.label}</span>
            {n.id === "alerts" && alertCount > 0 && <span className="counter">{alertCount}</span>}
            {estaEmDesenvolvimento(ID_TO_ROUTE[n.id]) && <span className="nav-em-breve">Em breve</span>}
          </NavLink>
        ))}
        {admin.length > 0 && (
          <>
            <div className="nav-group-label">Admin</div>
            {admin.map(n => (
              <NavLink key={n.id} to={ID_TO_ROUTE[n.id] || '/'} onClick={onNavigate}
                className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
                <Icon name={n.icon} size={16} />
                <span>{n.label}</span>
                {estaEmDesenvolvimento(ID_TO_ROUTE[n.id]) && <span className="nav-em-breve">Em breve</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="sidebar-foot">
        <NavLink to="/perfil" onClick={onNavigate} className={({ isActive }) => `sidebar-me${isActive ? " active" : ""}`} title="Meu perfil">
          <div className="avatar">{store.me?.initials}</div>
          <div className="who">
            <div className="n">{store.me?.nome}</div>
            <div className="r">{(ROLE_LABELS && ROLE_LABELS[papel]) || papel}</div>
          </div>
        </NavLink>
        <button className="icon-btn" title="Sair" onClick={() => encerrarSessao()}>
          <Icon name="log-out" size={15} />
        </button>
      </div>
    </aside>
  );
}

function QuickSearch({ onOpen }) {
  return (
    <button className="quicksearch" onClick={onOpen}>
      <Icon name="search" size={15} />
      <span>Buscar leads, empresas, agentes…</span>
      <span className="kbd">
        <kbd>Ctrl</kbd>
        <kbd>K</kbd>
      </span>
    </button>
  );
}

// ===== Notification Bell Dropdown =====
// Aceitar/recusar convite de tarefa, direto no sino. So o proprio participante
// consegue (RLS da 0063); a resposta vem de `meusConvites`, carregado do banco.
// Sem linha em meusConvites = vinculo removido: nao ha o que responder.
function RespostaConvite({ tarefaId }) {
  const store = useStore();
  const st = (store.meusConvites || {})[tarefaId];
  const parar = (fn) => (e) => { e.stopPropagation(); fn(); };
  if (st === "pendente") {
    return (
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <button type="button" className="btn sm primary" onClick={parar(() => store.responderConvite(tarefaId, true))}>Aceitar</button>
        <button type="button" className="btn sm" onClick={parar(() => store.responderConvite(tarefaId, false))}>Recusar</button>
      </div>
    );
  }
  if (st === "aceito") {
    return (
      <div style={{ fontSize: 11, marginTop: 4, color: "var(--color-ok)" }}>
        Confirmado ·{" "}
        <button type="button" className="link-btn" style={{ fontSize: 11, color: "var(--color-ink-soft)", textDecoration: "underline", background: "none", border: 0, padding: 0, cursor: "pointer" }}
          onClick={parar(() => store.responderConvite(tarefaId, false))}>não vou participar</button>
      </div>
    );
  }
  if (st === "recusado") return <div style={{ fontSize: 11, marginTop: 4, color: "var(--color-ink-soft)" }}>Você recusou</div>;
  return null;
}

function NotificationBell({ onOpenAlerts, onOpenLead }) {
  const store = useStore();
  const [open, setOpen] = useStateShell(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pending = store.alerts.filter(a => !a.feito);
  const recent = pending.slice(0, 5);
  const notifs = (store.notifications || []).slice(0, 6);
  const unread = (store.notifications || []).filter(n => !n.lida).length;
  // Convite esperando resposta tambem acende o sino: ler a notificacao nao e
  // responder, e o pendente continua pedindo acao depois de lido.
  const convitesPendentes = Object.values(store.meusConvites || {}).filter(st => st === "pendente").length;
  const count = pending.length + unread + convitesPendentes;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) store.markNotificationsRead();
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="icon-btn" title="Notificações" onClick={toggle}>
        <Icon name="bell" size={16} />
        {count > 0 && <span className="dot" />}
      </button>
      {open && (
        <div className="notif-dropdown" style={{
          position: "absolute", right: 0, top: 42,
          width: 372, background: "var(--color-surface)",
          borderRadius: "var(--radius-md)", border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-pop)", zIndex: 120, overflow: "hidden",
        }}>
          {notifs.length > 0 && (
            <>
              <div style={{ padding: "11px 14px 7px", display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="at-sign" size={14} style={{ color: "var(--color-accent-deep)" }} />
                <div style={{ fontWeight: 700, fontSize: 13 }}>Menções e atribuições</div>
              </div>
              <div style={{ maxHeight: 190, overflowY: "auto", borderBottom: "1px solid var(--color-border)" }}>
                {notifs.map(n => {
                  const lead = n.leadId ? store.leads.find(l => l.id === n.leadId) : null;
                  return (
                    <div key={n.id} style={{ padding: "9px 14px", borderTop: "1px solid var(--color-border)", cursor: lead ? "pointer" : "default", display: "flex", gap: 10, background: n.lida ? "transparent" : "var(--color-accent-soft)" }}
                      onClick={() => { if (lead) { setOpen(false); onOpenLead(lead.id); } }}>
                      <div style={{ width: 28, height: 28, borderRadius: "var(--radius-sm)", background: n.tipo === "mencao" ? "var(--color-accent-soft)" : "var(--color-ok-soft)", color: n.tipo === "mencao" ? "var(--color-accent-deep)" : "var(--color-ok)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <Icon name={n.tipo === "mencao" ? "at-sign" : n.tipo === "repasse" ? "send" : n.tipo === "convite" ? "calendar-plus" : "check-circle-2"} size={13} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.35 }}>{n.titulo}</div>
                        {/* "· sistema + e-mail" saiu daqui em 15/09: nenhum
                            e-mail era mandado, e esta linha era a única das três
                            instâncias da mentira que o usuário de fato LIA.
                            `userNome` também saiu — é sempre quem está olhando o
                            próprio sino. No lugar entra QUANDO, que só passou a
                            significar algo agora que a notificação sobrevive ao
                            F5: antes era "agora" para todas. */}
                        <div style={{ fontSize: 11, color: "var(--color-ink-soft)" }}>{n.contexto}{n.contexto && n.when ? " · " : ""}{n.when}</div>
                        {n.origemTipo === "tarefa" && <RespostaConvite tarefaId={n.origemId} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="check-circle-2" size={15} />
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>Tarefas</div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, background: "var(--color-bg)", padding: "1px 6px", borderRadius: 4, color: "var(--color-ink-soft)" }}>{pending.length} pendentes</span>
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {recent.length === 0 && (
              <div className="empty" style={{ padding: 30 }}>
                <Icon name="check-circle-2" size={24} style={{ color: "var(--color-ok)", marginBottom: 6 }} />
                <div style={{ fontWeight: 600 }}>Tudo em dia</div>
                <div style={{ fontSize: 11.5, marginTop: 2 }}>Nenhuma tarefa pendente.</div>
              </div>
            )}
            {recent.map(a => {
              const lead = store.leads.find(l => l.id === a.leadId);
              return (
                <div key={a.id} style={{ padding: "10px 14px", borderBottom: "1px solid var(--color-border)", cursor: "pointer", display: "flex", gap: 10 }}
                  onClick={() => { setOpen(false); if (lead) onOpenLead(lead.id); }}>
                  <div style={{ width: 30, height: 30, borderRadius: "var(--radius-sm)", background: a.vencido ? "var(--color-danger-soft)" : "var(--color-warn-soft)", color: a.vencido ? "var(--color-danger)" : "var(--color-warn)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                    <Icon name={a.vencido ? "alert-triangle" : "bell"} size={13} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 12.5 }}>{a.tipo}</div>
                    <div style={{ fontSize: 11.5, color: "var(--color-ink-soft)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead?.empresa} · {a.quando}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding: 10, borderTop: "1px solid var(--color-border)", background: "var(--color-bg)" }}>
            <button className="btn sm" style={{ width: "100%", justifyContent: "center" }} onClick={() => { setOpen(false); onOpenAlerts(); }}>
              Ver todas as tarefas <Icon name="arrow-right" size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Header({ pageTitle, pageSub, alertCount, onOpenCmd, onOpenAlerts, onOpenLead, onToggleMenu }) {
  const meHeader = useStore().me;
  const navHeader = useNavigate();
  return (
    <header className="header">
      <button className="icon-btn menu-btn" onClick={onToggleMenu} aria-label="Menu" style={{ marginRight: 2 }}>
        <Icon name="menu" size={18} />
      </button>
      <div className="page-title">
        <span>{pageTitle}</span>
        {pageSub && <><span className="sep">/</span><span className="sub">{pageSub}</span></>}
      </div>
      <QuickSearch onOpen={onOpenCmd} />
      <div className="header-actions">
        <NotificationBell onOpenAlerts={onOpenAlerts} onOpenLead={onOpenLead} />
        <div style={{ width: 1, height: 22, background: "var(--color-border)", margin: "0 4px" }} />
        <button className="avatar avatar-btn" title="Meu perfil" onClick={() => navHeader('/perfil')}>{meHeader?.initials}</button>
      </div>
    </header>
  );
}

function FAB({ onAction }) {
  const [open, setOpen] = useStateShell(false);
  const perms = usePermissions('pipeline.create_lead', 'clientes.create', 'tarefas.create');
  const actions = [
    { id: "lead",    label: "Novo Negócio",   icon: "plus",           perm: 'pipeline.create_lead' },
    { id: "cliente", label: "Novo Cliente",   icon: "users",          perm: 'clientes.create' },
    { id: "alert",   label: "Criar Tarefa",   icon: "check-circle-2", perm: 'tarefas.create' },
  ].filter(a => perms[a.perm]);
  if (!actions.length) return null;
  return (
    <>
      {open && (
        <div className="fab-menu">
          {actions.map(a => (
            <button key={a.id} className="fab-item" onClick={() => { setOpen(false); onAction(a.id); }}>
              <span className="ic"><Icon name={a.icon} size={15} /></span>
              {a.label}
            </button>
          ))}
        </div>
      )}
      <button className="fab" onClick={() => setOpen(o => !o)} aria-label="Ações rápidas" style={{ transform: open ? "rotate(45deg)" : "none", transition: "transform 0.18s" }}>
        <Icon name="plus" size={22} strokeWidth={2.2} />
      </button>
    </>
  );
}

// Command palette
// `buscarPedido` chega por prop, vinda de app.jsx: shell.jsx não fala com o
// banco direto, pelo mesmo motivo de store.jsx e data.jsx (ver §5 do CLAUDE.md).
// Sem a prop, a busca segue funcionando só com negócios, como antes.
function CommandPalette({ open, onClose, onNav, onOpenLead, leads, buscarPedido }) {
  const store = useStore();
  const papelAtual = (store.me?.papel ?? 'admin').toLowerCase();
  const [q, setQ] = useStateShell("");
  const [sel, setSel] = useStateShell(0);
  // Reset apenas quando o palette ABRE — não a cada re-render do app (realtime/toasts),
  // o que antes apagava a busca digitada.
  useEffect(() => {
    if (open) { setQ(""); setSel(0); }
  }, [open]);
  // Listener de ESC, independente da identidade de onClose.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Pedidos vêm do BANCO, não da memória: são 4.190 e crescendo, e carregar tudo
  // no navegador para achar um é o tipo de coisa que funciona na demonstração e
  // trava na operação. Debounce de 250ms para não disparar consulta a cada tecla.
  const [pedidoHits, setPedidoHits] = useStateShell([]);
  useEffect(() => {
    if (!open || !buscarPedido || q.trim().length < 3) { setPedidoHits([]); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await buscarPedido(q);
        if (vivo) setPedidoHits(r || []);
      } catch (e) {
        // Falha de busca não pode ser silenciosa (§5): sem isto, RLS recusada
        // pareceria "nenhum pedido encontrado".
        console.error('Busca de pedido falhou:', e?.message || e);
        if (vivo) setPedidoHits([]);
      }
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [open, q, buscarPedido]);

  if (!open) return null;
  const ql = q.toLowerCase();
  const leadsToSearch = leads || LEADS;
  // Painéis primeiro: garante que as opções de navegação fiquem sempre visíveis.
  const navHits = NAV.filter(n => navAllowedFor(papelAtual, n.id) && (!q || n.label.toLowerCase().includes(ql))).slice(0, 8);
  // Menos sugestões de leads para não empurrar os painéis para fora da área visível.
  const leadHits = leadsToSearch.filter(l =>
    !q || l.empresa.toLowerCase().includes(ql) || l.contato.toLowerCase().includes(ql)
  ).slice(0, q ? 5 : 3);

  return (
    <div className="cmd-back" onClick={onClose}>
      <div className="cmd" onClick={e => e.stopPropagation()}>
        <div className="cmd-input">
          <Icon name="search" size={18} />
          <input autoFocus placeholder="Buscar negócios ou ir para um painel…" value={q} onChange={e => setQ(e.target.value)} />
          <kbd style={{ fontSize: 10.5, padding: "2px 6px", border: "1px solid var(--color-border)", borderRadius: 4, fontFamily: "var(--font-mono)" }}>ESC</kbd>
        </div>
        <div className="cmd-list">
          {navHits.length > 0 && (
            <>
              <div className="cmd-section">Painéis · ir para</div>
              {navHits.map(n => (
                <div key={n.id} className="cmd-item" onClick={() => { onNav(n.id); onClose(); }}>
                  <Icon name={n.icon} size={14} className="ic" />
                  <div className="nm">{n.label}</div>
                  <div className="sub">Painel</div>
                </div>
              ))}
            </>
          )}
          {leadHits.length > 0 && (
            <>
              <div className="cmd-section">{q ? `Negócios · ${leadHits.length}` : "Negócios recentes"}</div>
              {leadHits.map(l => (
                <div key={l.id} className="cmd-item" onClick={() => { onOpenLead(l.id); onClose(); }}>
                  <Icon name="circle-dot" size={14} className="ic" />
                  <div>
                    <div className="nm">{l.empresa}</div>
                    <div style={{ fontSize: 11, color: "var(--color-ink-soft)" }}>{l.contato} · {STAGE_NAMES[l.stage]}</div>
                  </div>
                  <div className="sub" style={{ fontFamily: "var(--font-mono)" }}>{l.valor > 0 ? brlCompact(l.valor) : ""}</div>
                </div>
              ))}
            </>
          )}
          {pedidoHits.length > 0 && (
            <>
              <div className="cmd-section">Pedidos · {pedidoHits.length}</div>
              {pedidoHits.map(p => (
                <div key={p.id} className="cmd-item">
                  <Icon name="file-text" size={14} className="ic" />
                  <div>
                    <div className="nm" style={{ fontFamily: "var(--font-mono)" }}>{p.codigo_pedido}</div>
                    <div style={{ fontSize: 11, color: "var(--color-ink-soft)" }}>
                      {/* `via` diz por qual referência o pedido foi encontrado: quem
                          digitou a chave do DANFE precisa ver que veio pela nota. */}
                      achado por {p.via}
                      {p.data_emissao ? ` · emitido em ${new Date(p.data_emissao + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}
                    </div>
                  </div>
                  <div className="sub" style={{ fontFamily: "var(--font-mono)" }}>{p.valor_total > 0 ? brlCompact(p.valor_total) : ""}</div>
                </div>
              ))}
            </>
          )}
          {leadHits.length === 0 && navHits.length === 0 && (
            <div className="empty" style={{ padding: 30 }}>
              <Icon name="search" size={22} style={{ color: "var(--color-ink-faint)", marginBottom: 8 }} />
              <div>Nenhum resultado para "<strong>{q}</strong>"</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { Sidebar, NotificationBell, Header, FAB, CommandPalette };
